// The rows of the rail, as DOM. `src/derive/axis.ts` decides what the rows are,
// `src/axis/axis-view.ts` decides where they go, and this file is the only
// place one becomes an element.
//
// Split out of `axis-view.ts` (SPEC-viewfinder V3): the rail's own file owns
// the scroll, the anchoring and the rebuild, and the elements a rebuild builds
// are a separate concern that was making it the longest file in `src/`.
import { relativeTime, subLine } from '../derive/axis.ts';
import { column } from '../derive/order.ts';
import { TAG } from '../derive/voices.ts';
import { clockLabel } from '../shared/format.ts';
import { distanceLabel } from '../shared/geo.ts';
import type { AxisRow, Voice } from '../shared/model.ts';
import { moveCentre } from '../state/commands.ts';
import { radiusIncluding, viewState, type ViewState } from '../state/viewstate.ts';
import { renderDetail, type DetailContext } from './detail.ts';

/** A hold this long leaves the voice solo; the click that follows is swallowed (D-d). */
const HOLD_MS = 500;

/** The thin line a silenced voice leaves, in the place of the rows it replaced (§5.5). */
export const RUN_ROW = 'row--run';

/** „starsze · N wpisów" — the head of the old past, and the one row that opens it. */
export const FOLD_ROW = 'row--fold';

export const el = <K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  text = '',
): HTMLElementTagNameMap[K] => {
  const node = document.createElement(tag);
  node.className = className;
  if (text) node.textContent = text;
  return node;
};

/**
 * The voice tag: tap mutes, a 500 ms hold leaves that voice solo, Enter or Space
 * mutes and Shift+Enter solos (D-d, D-e). Muting is a view change with nothing
 * opened, so it is a `'replace'` write and survives Back and a reload the way a
 * „Warstwy" switch does (§5.7, ruling 8); solo opens, so Back undoes it.
 */
function voiceTag(voice: Voice, view: ViewState): HTMLButtonElement {
  const tag = el('button', 'row__voice mono', TAG[voice]);
  tag.type = 'button';
  const muted = view.muted.includes(voice);
  tag.setAttribute(
    'aria-label',
    `${TAG[voice]}: dotknij, żeby wyciszyć; przytrzymaj, żeby zostawić tylko ten głos`,
  );
  tag.setAttribute('aria-pressed', String(muted || view.solo === voice));
  tag.setAttribute('aria-keyshortcuts', 'Shift+Enter');
  if (muted) tag.dataset.muted = 'true';

  const mute = () => {
    viewState.set(
      { muted: muted ? view.muted.filter((v) => v !== voice) : [...view.muted, voice] },
      'replace',
    );
  };
  const solo = () => {
    viewState.set({ solo: view.solo === voice ? null : voice }, 'open');
  };

  let held: ReturnType<typeof setTimeout> | null = null;
  let fired = false;
  const stop = () => {
    if (held) clearTimeout(held);
    held = null;
    delete tag.dataset.holding;
  };
  tag.addEventListener('pointerdown', () => {
    fired = false;
    tag.dataset.holding = 'true';
    held = setTimeout(() => {
      fired = true;
      stop();
      solo();
    }, HOLD_MS);
  });
  tag.addEventListener('pointerup', stop);
  tag.addEventListener('pointercancel', stop);
  tag.addEventListener('pointerleave', stop);
  tag.addEventListener('click', (e) => {
    // One gesture, one result: the click that follows a fired hold would
    // otherwise mute the voice it just soloed (D-d).
    if (fired) {
      fired = false;
      e.preventDefault();
      return;
    }
    mute();
  });
  tag.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    if (e.shiftKey) solo();
    else mute();
  });
  return tag;
}

/** One item row: time, body, distance, with the detail expanding inside it. */
function itemRow(row: Extract<AxisRow, { kind: 'item' }>, view: ViewState, ctx: DetailContext, now: number) {
  const { item } = row;
  const li = el('li', 'row');
  li.dataset.id = item.id;
  li.dataset.side = row.side;
  li.dataset.voice = item.voice;
  // Whether we trust this fix, ON the row rather than looked up: the live half
  // of a gesture re-spells this row's distance under the finger and has to keep
  // the `?` the derivation put there (§4.1, `./live-rows.ts`).
  if (item.uncertain) li.dataset.uncertain = 'true';
  // §4.4's scale: a past row is not less important, it is less about now.
  li.style.setProperty('--weight', String(item.weight));

  // The one left column (§2.4): the value the list is ordered by on top, the
  // other under it. Which is which is the ORDER's business, not the view's, so
  // it is `derive/order.ts` that says.
  const { primary, secondary } = column(row, view.order);
  const when = el('div', 'row__time mono');
  when.append(el('span', 'row__value', primary), el('span', 'row__other', secondary));
  li.append(when);

  const body = el('div', 'row__body');
  const open = view.selected === item.id;

  const title = el('button', 'row__title');
  title.type = 'button';
  title.setAttribute('aria-expanded', String(open));
  // A ZTM notice wears no glyph in the prototype — it is a sentence, not a
  // thing on the map — and `src/data/data.ts` gives every alert kind the same `○`.
  if (item.kind !== 'notice') {
    const glyph = el('span', 'row__glyph', item.glyph);
    glyph.setAttribute('aria-hidden', 'true');
    title.append(glyph);
  }
  title.append(el('span', 'row__label', item.title));
  title.addEventListener('click', () => {
    if (open) viewState.closeDetail();
    else viewState.set({ selected: item.id }, 'open');
  });
  // The row is the keyboard path, so focusing it highlights its mark (D-f).
  title.addEventListener('focus', () => {
    li.dataset.focused = 'true';
  });
  title.addEventListener('blur', () => {
    delete li.dataset.focused;
  });
  body.append(title, voiceTag(item.voice, view));

  const line = subLine(item, now);
  const sub = el('div', 'row__sub');
  if (line.a) {
    const a = el('span', 'row__sub-a', line.a);
    a.dataset.tone = item.sub.tone;
    sub.append(a);
  }
  if (line.b) sub.append(el('span', 'row__sub-b', line.a ? ` · ${line.b}` : line.b));
  if (sub.childNodes.length) body.append(sub);

  if (open) {
    li.dataset.open = 'true';
    body.append(renderDetail(item, ctx));
  }
  li.append(body);
  return li;
}

/**
 * The thin line a silenced voice leaves in the place of its rows. Tapping it
 * restores that voice — the prototype's own handler throws, and the help screen
 * teaches the affordance anyway, so the help screen wins (D-a, §5.5).
 */
function muteRunRow(
  row: Extract<AxisRow, { kind: 'muteRun' }>,
  view: ViewState,
  now: number,
  moved: boolean,
) {
  const li = el('li', 'row row--run');
  li.dataset.side = row.side;
  const button = el('button', 'run mono', '');
  button.type = 'button';
  // The run keeps the time column of the rows it replaces: it stands in their
  // place on the axis, and a line with no hour would float.
  // The clock rides inside the button, as it does in the prototype, but it is
  // not part of what the control is called: „AQ · 3 wpisy · wyciszone" is.
  const when = el('span', 'run__time', moved ? clockLabel(row.at) : relativeTime(row.at, now));
  when.setAttribute('aria-hidden', 'true');
  button.append(when, el('span', 'run__label', row.label));
  button.addEventListener('click', () => {
    if (row.reason === 'muted') {
      viewState.set({ muted: view.muted.filter((v) => !row.voices.includes(v)) }, 'replace');
      return;
    }
    // „poza solo" has two causes, and the run restores whichever one hid it:
    // the line lens first, because it is the narrower of the two.
    if (view.onlyLine != null) viewState.set({ onlyLine: null }, 'replace');
    else viewState.set({ solo: null }, 'replace');
  });
  li.append(button);
  return li;
}

/** A source that is not answering, in the place where its rows would be (§7.2). */
function noteRow(row: Extract<AxisRow, { kind: 'note' }>, now: number, moved: boolean) {
  const li = el('li', 'row row--note');
  li.append(el('div', 'row__time mono', moved ? clockLabel(row.at) : relativeTime(row.at, now)));
  // The tag is a sibling of the sentence, not its first word: a note is read
  // out as what it says, and „AQ" is the picture of who is saying it. It is a
  // plain span rather than a button, because the mute for this voice is on its
  // own rows and two controls with one name would be ambiguous.
  const body = el('div', 'note');
  const tag = el('span', 'note__voice mono', TAG[row.voice]);
  tag.setAttribute('aria-hidden', 'true');
  const text = el('p', 'note__text', row.text);
  text.dataset.tone = row.tone;
  if (row.connecting) text.dataset.connecting = 'true';
  body.append(tag, text);
  li.append(body);
  return li;
}

/** The band a silence leaves on the axis: hatch, brick dashes, one sentence. */
function gapRow(row: Extract<AxisRow, { kind: 'gap' }>) {
  const li = el('li', 'row row--gap');
  li.setAttribute('role', 'status');
  // The clock is the only mono thing in the band; the label is Archivo bold in
  // brick and the sentence under it is ordinary ink (prototype, `row.isGap`).
  li.append(el('div', 'row__time mono', clockLabel(row.at)));
  const body = el('div', 'gap');
  body.append(el('p', 'gap__label', row.label), el('p', 'gap__sub', row.sub));
  li.append(body);
  return li;
}

/**
 * „starsze · 4 wpisy" — the old past, behind one line at the head of the axis,
 * in the same visual language as a mute run. A button, so it opens in place;
 * whether it is open is this rail's business and belongs in neither the address
 * nor this browser's memory, because it is not a view a resident would want a
 * link to reproduce.
 */
export function foldRow(label: string, open: boolean, toggle: () => void) {
  const li = el('li', `row ${RUN_ROW} ${FOLD_ROW}`);
  const button = el('button', 'run mono', '');
  button.type = 'button';
  button.setAttribute('aria-expanded', String(open));
  button.append(el('span', 'run__time', ''), el('span', 'run__label', label));
  button.addEventListener('click', toggle);
  li.append(button);
  return li;
}

/** „teraz 14:00" — only once the axis is reading another hour (§4.4, dossier §4.9). */
function tickRow(row: Extract<AxisRow, { kind: 'tick' }>, now: number) {
  const li = el('li', 'row row--tick mono');
  // „teraz 14:00 ——— −1 h": the label, the rule, then how far from here it is.
  // It says nothing a screen reader has not already read off the rows around it.
  li.setAttribute('aria-hidden', 'true');
  li.append(el('span', 'tick__label', row.label));
  li.append(el('div', 'row__time', relativeTime(row.at, now)));
  return li;
}

/** The nearest thing outside the ring — what the empty-ring sentence names. */
export type Nearest = { title: string; metres: number; position: { lat: number; lon: number } };

/**
 * „W promieniu 3,0 km nic nie ma. Najbliżej: <tytuł> · 3,2 km" — the one row
 * that stands in place of the rows when nothing positioned is inside the ring
 * (SPEC-viewfinder §2.6).
 *
 * One sentence in one ink, on the same grid as an item row, with a mono „—" in
 * the left column — the approved artboard's shape (`phone-empty-ring.html`), so
 * the words start where every row body starts. It reads as a row of the list
 * that happens to have no place, which is what it is.
 *
 * It is a button, and the owner ruled what it does: widen the radius to the
 * smallest 10 m step that includes that item, or — past the 5 km ceiling `r`
 * carries — pan the centre to it at the radius it already has. One `'replace'`
 * write for the radius, one `'open'` write for the pan, which is the rule every
 * other radius and centre change obeys.
 *
 * Both distances are spelled by `distanceLabel` and by nothing else: the
 * fitness function „nothing outside `src/shared/geo.ts` spells a distance"
 * covers this sentence too (§7.4).
 *
 * With nothing positioned anywhere in the city there is nothing to name and
 * nothing to widen to, so the sentence is its first clause alone and is not a
 * button — the owner approved that form on 2026-09-21 (§5).
 */
export function emptyRingRow(radius: number, nearest: Nearest | null) {
  const li = el('li', 'row row--empty');
  const said = `W promieniu ${distanceLabel(radius)} nic nie ma.`;
  const dash = el('span', 'row__time mono', '—');
  dash.setAttribute('aria-hidden', 'true');

  // The same shape either way — a `<p>` where there is nothing to do and a
  // `<button>` where there is — so one rule draws both and the sentence sits
  // on the row grid in both. The city-wide form is the owner's, 2026-09-21:
  // the first clause alone, and not a button, because there is nothing to
  // name and nothing to widen to (§5).
  if (!nearest) {
    const text = el('p', 'empty');
    text.append(dash, el('span', 'empty__text', said));
    li.append(text);
    return li;
  }

  const button = el('button', 'empty');
  button.type = 'button';
  button.append(
    dash,
    el('span', 'empty__text', `${said} Najbliżej: ${nearest.title} · ${distanceLabel(nearest.metres)}`),
  );
  button.addEventListener('click', () => {
    const wider = radiusIncluding(nearest.metres);
    // Past the ceiling there is no radius that reaches it, so the centre goes
    // instead. `moveCentre` is the one writer of a picked centre (§1.4), and it
    // clears a borrowed hour with it, as „stąd" always does (D-p).
    if (wider == null) moveCentre(nearest.position);
    else viewState.set({ radius: wider }, 'replace');
  });
  li.append(button);
  return li;
}

/**
 * A row, as the element that draws it. One place that knows which builder each
 * `kind` belongs to, so the rail can be about where the rows GO.
 */
export function rowElement(
  row: AxisRow,
  view: ViewState,
  when: { now: number; moved: boolean },
  contextFor: (item: Extract<AxisRow, { kind: 'item' }>) => DetailContext,
): HTMLElement {
  const { now, moved } = when;
  if (row.kind === 'item') return itemRow(row, view, contextFor(row), now);
  if (row.kind === 'muteRun') return muteRunRow(row, view, now, moved);
  if (row.kind === 'note') return noteRow(row, now, moved);
  if (row.kind === 'gap') return gapRow(row);
  return tickRow(row, now);
}
